import { enUS, plPL } from "@clerk/localizations";
import { enUS as dateFnsEn, pl as dateFnsPl } from "date-fns/locale";

export type Locale = "pl" | "en";

export const DEFAULT_LOCALE: Locale = "pl";
const LOCALE_COOKIE_NAME = "parrot-locale";
const LOCALE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
let currentLocale: Locale | null = null;

type LocaleMatch = {
  data?: unknown;
  handle?: unknown;
};

export type LocaleHandle = {
  locale?: Locale;
  getLocale?: (match: LocaleMatch) => Locale | null | undefined;
};

export const DATA_LOCALE_HANDLE = {
  getLocale(match) {
    const locale = (match.data as { locale?: unknown } | null | undefined)
      ?.locale;
    return normalizeLocale(locale);
  },
} satisfies LocaleHandle;

export function resolveLocaleFromMatches(
  matches: LocaleMatch[],
  fallbackLocale: Locale | null | undefined = DEFAULT_LOCALE
): Locale {
  // Matched route handles can intentionally override the request locale,
  // for example the /en and /_pl marketing route trees.
  for (let index = matches.length - 1; index >= 0; index -= 1) {
    const locale = getLocaleFromMatch(matches[index]);
    if (locale) {
      return locale;
    }
  }

  return fallbackLocale ?? DEFAULT_LOCALE;
}

export function getLocaleFromRequest(request: Request): Locale {
  const cookieLocale = getLocaleFromCookieHeader(request.headers.get("Cookie"));
  if (cookieLocale) {
    return cookieLocale;
  }

  return (
    getLocaleFromAcceptLanguage(request.headers.get("Accept-Language")) ??
    DEFAULT_LOCALE
  );
}

export function getLocale(): Locale {
  return currentLocale ?? getLocaleFromDocument() ?? DEFAULT_LOCALE;
}

export function setCurrentLocale(locale: Locale): void {
  currentLocale = locale;
}

export function getLocaleTag(): string {
  return getLocale() === "pl" ? "pl-PL" : "en-US";
}

export function getDateFnsLocale() {
  return getLocale() === "pl" ? dateFnsPl : dateFnsEn;
}

function setLocaleCookie(locale: Locale): void {
  if (typeof document === "undefined") {
    return;
  }

  const expires = new Date();
  expires.setFullYear(expires.getFullYear() + 1);
  document.cookie = `${LOCALE_COOKIE_NAME}=${locale};path=/;expires=${expires.toUTCString()};max-age=${LOCALE_COOKIE_MAX_AGE};SameSite=Lax`;
}

export function setPreferredLocale(locale: Locale): void {
  setCurrentLocale(locale);
  setLocaleCookie(locale);
}

export function getClerkLocalization(locale: Locale | null | undefined) {
  return (locale ?? DEFAULT_LOCALE) === "pl" ? plPL : enUS;
}

function getLocaleFromMatch(match: LocaleMatch): Locale | null {
  const { handle } = match;
  if (!handle || typeof handle !== "object") {
    return null;
  }

  const localeFromProperty = normalizeLocale((handle as LocaleHandle).locale);
  if (localeFromProperty) {
    return localeFromProperty;
  }

  const getLocale = (handle as LocaleHandle).getLocale;
  if (typeof getLocale !== "function") {
    return null;
  }

  return normalizeLocale(getLocale(match));
}

function getLocaleFromDocument(): Locale | null {
  if (typeof document === "undefined") {
    return null;
  }

  return normalizeLocaleTag(document.documentElement.lang);
}

function getLocaleFromCookieHeader(cookieHeader: string | null): Locale | null {
  if (!cookieHeader) {
    return null;
  }

  for (const cookie of cookieHeader.split(";")) {
    const trimmedCookie = cookie.trim();
    if (!trimmedCookie) {
      continue;
    }

    const [key, ...valueParts] = trimmedCookie.split("=");
    if (key !== LOCALE_COOKIE_NAME) {
      continue;
    }

    try {
      return normalizeLocale(decodeURIComponent(valueParts.join("=")));
    } catch {
      return normalizeLocale(valueParts.join("="));
    }
  }

  return null;
}

function getLocaleFromAcceptLanguage(
  acceptLanguageHeader: string | null
): Locale | null {
  if (!acceptLanguageHeader) {
    return null;
  }

  const candidates = acceptLanguageHeader
    .split(",")
    .map((part, index) => {
      const [languageTag, ...params] = part.split(";");
      const locale = normalizeLocaleTag(languageTag);
      if (!locale) {
        return null;
      }

      const quality =
        params
          .map((param) => param.trim())
          .find((param) => param.startsWith("q="))
          ?.slice(2) ?? "1";
      const parsedQuality = Number.parseFloat(quality);

      if (Number.isNaN(parsedQuality) || parsedQuality <= 0) {
        return null;
      }

      return {
        locale,
        quality: Math.min(parsedQuality, 1),
        index,
      };
    })
    .filter((candidate) => candidate !== null)
    .sort((left, right) => {
      if (right.quality !== left.quality) {
        return right.quality - left.quality;
      }

      return left.index - right.index;
    });

  return candidates[0]?.locale ?? null;
}

function normalizeLocaleTag(value: string): Locale | null {
  const [primaryTag] = value.trim().toLowerCase().split(/[-_]/);
  return normalizeLocale(primaryTag);
}

function normalizeLocale(value: unknown): Locale | null {
  return value === "pl" || value === "en" ? value : null;
}
